// src/components/Attendance/LiveKioskSection.js (FINAL & CORRECTED)

import React, { useState, useEffect, useRef } from 'react';
import { useFirebase } from '../../App';
import { collection, query, where, orderBy, onSnapshot, getDoc, doc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

// --- FACE-API.JS IMPORT ---
import * as faceapi from 'face-api.js';

function LiveKioskSection() {
    const { db, faceApiModelsLoaded } = useFirebase(); 
    
    const videoRef = useRef(null);
    const [employees, setEmployees] = useState([]);
    const [logData, setLogData] = useState([]);
    const [statusText, setStatusText] = useState('Camera is Offline');
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [stream, setStream] = useState(null);
    const [recognizedEmployee, setRecognizedEmployee] = useState(null);
    const [labeledDescriptors, setLabeledDescriptors] = useState(null);
    const detectionInterval = useRef(null);
    const [time, setTime] = useState(new Date());
    const [currentLocation, setCurrentLocation] = useState({ status: 'idle', data: null });
    
    const toYYYYMMDD = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const [filterDate, setFilterDate] = useState(toYYYYMMDD(new Date()));

    useEffect(() => { const timerId = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(timerId); }, []);
    
    useEffect(() => {
        if (!db) { setLabeledDescriptors(null); return; }
        if (!faceApiModelsLoaded) { setStatusText("Loading face detection models..."); setLabeledDescriptors(null); return; }

        // Corrected collection path: No artifacts/{appId}/
        const q = query(collection(db, `workers`)); 
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedEmployees = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setEmployees(fetchedEmployees);
            
            const descriptors = fetchedEmployees
                .filter(emp => emp.faceDescriptor && emp.faceDescriptor.length > 0)
                .map(emp => new faceapi.LabeledFaceDescriptors(emp.employeeId, [new Float32Array(emp.faceDescriptor)])); 
            setLabeledDescriptors(descriptors);
            setStatusText(isCameraOn ? 'System Ready.' : 'Employee data loaded. Ready to start camera.');
        });
        return () => unsubscribe();
    }, [db, isCameraOn, faceApiModelsLoaded]); 
    
    useEffect(() => {
        if(!db) { setLogData([]); return; }
        // Corrected collection path: No artifacts/{appId}/
        const q = query(collection(db, `attendance`), where("date", "==", filterDate), orderBy("checkIn", "desc")); 
        const unsubscribe = onSnapshot(q, (snapshot) => { setLogData(snapshot.docs.map(doc => ({...doc.data(), id: doc.id}))); });
        return unsubscribe;
    }, [db, filterDate]);
    
    useEffect(() => { return () => { if (stream) stream.getTracks().forEach(track => track.stop()); if (detectionInterval.current) clearInterval(detectionInterval.current); }; }, [stream]);
    
    const getLocation = () => {
        setCurrentLocation({ status: 'fetching', data: null });
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) { setCurrentLocation({ status: 'error', data: 'Geolocation is not supported.' }); return reject(new Error("Geolocation is not supported.")); }
            navigator.geolocation.getCurrentPosition( async (position) => {
                const { latitude, longitude } = position.coords;
                try { 
                    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`); 
                    const data = await response.json(); 
                    const address = data.address || {}; 
                    const locationHierarchy = [ address.shop, address.amenity, address.building, address.road, address.neighbourhood, address.suburb, address.quarter, address.city, address.town, address.village ]; 
                    const mostSpecificName = locationHierarchy.find(name => name) || 'Unknown Area'; 
                    const cityName = address.city || address.town || address.village || ''; 
                    const displayName = (mostSpecificName !== cityName && cityName) ? `${mostSpecificName}, ${cityName}` : mostSpecificName; 
                    const locationData = { latitude, longitude, name: displayName }; 
                    setCurrentLocation({ status: 'success', data: locationData }); 
                    resolve(locationData); 
                } catch (apiError) { 
                    const locationData = { latitude, longitude, name: 'Coordinates Acquired (Name not found)' }; 
                    setCurrentLocation({ status: 'success', data: locationData }); 
                    resolve(locationData); 
                } 
            }, (error) => { setCurrentLocation({ status: 'error', data: error.message }); reject(error); } );
        });
    };

    const startDetection = () => { 
        if (!labeledDescriptors || labeledDescriptors.length === 0) { setStatusText("No employee faces registered."); return; } 
        const faceMatcher = new faceapi.FaceMatcher(labeledDescriptors, 0.5); 
        detectionInterval.current = setInterval(async () => { 
            if (!videoRef.current || recognizedEmployee || videoRef.current.paused) return; 
            setStatusText("Searching for faces..."); 
            const detection = await faceapi.detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions()).withFaceLandmarks().withFaceDescriptor();
            if (detection) { 
                const result = faceMatcher.findBestMatch(detection.descriptor); 
                if (result.label !== 'unknown') { 
                    const employee = employees.find(e => e.employeeId === result.label); 
                    if (employee) { 
                        setRecognizedEmployee(employee); 
                        setStatusText(`Recognized: ${employee.fullName}`); 
                        clearInterval(detectionInterval.current); 
                    }
                } else { setStatusText("Unknown person detected."); } 
            } else { setStatusText("No face detected."); } 
        }, 1500);
    };
    
    const handleVideoPlay = () => { setStatusText('System Ready.'); startDetection(); };
    const startWebcam = async () => { 
        if (!faceApiModelsLoaded) { toast.error("Face detection models are not loaded yet. Please wait."); return; }

        if (stream) stream.getTracks().forEach(track => track.stop()); 
        setStatusText('Requesting camera...'); 
        try { 
            const newStream = await navigator.mediaDevices.getUserMedia({ video: true }); 
            if (videoRef.current) videoRef.current.srcObject = newStream; 
            setStream(newStream); setIsCameraOn(true); 
            await getLocation(); 
        } catch (err) { setStatusText('Webcam Access Denied.'); setIsCameraOn(false); } 
    };
    const stopWebcam = () => { if (stream) stream.getTracks().forEach(track => track.stop()); if (videoRef.current) videoRef.current.srcObject = null; setStream(null); setStatusText('Camera is Offline.'); setIsCameraOn(false); clearInterval(detectionInterval.current); setRecognizedEmployee(null); setCurrentLocation({ status: 'idle', data: null }); };
    const resetKiosk = () => { setRecognizedEmployee(null); if (isCameraOn) { setStatusText('System Ready.'); startDetection(); }};

    const handleAttendanceAction = async (action) => {
        if (!recognizedEmployee) return;
        const employee = recognizedEmployee;
        const now = new Date();
        const todayStr = toYYYYMMDD(now);
        const logId = `${todayStr}_${employee.id}`;
        const logRef = doc(db, 'attendance', logId);

        let locationData = currentLocation.data;
        if (!locationData) {
            try { locationData = await getLocation(); } 
            catch (locError) { toast.error("Could not get location. Please enable services."); return; }
        }

        if (action === 'check-in') {
            const currentLogSnap = await getDoc(logRef);
            if (currentLogSnap.exists()) { toast.error(`${employee.fullName} already checked in.`); } 
            else {
                let status = 'Present';
                if (now.getDay() === 0) { status = 'Holiday'; } 
                else {
                    const firstShift = employee.dutyShifts?.[0];
                    if (firstShift?.start) {
                        const [shiftHour, shiftMinute] = firstShift.start.split(':').map(Number);
                        if (now.getHours() > shiftHour || (now.getHours() === shiftHour && now.getMinutes() > (shiftMinute + 15))) { status = 'Late'; }
                    }
                }
                await setDoc(logRef, { employeeId: employee.id, fullName: employee.fullName, photoURL: employee.photoURL || '', date: todayStr, checkIn: Timestamp.now(), checkOut: null, status, checkInLocation: locationData });
                toast.success(`${employee.fullName} checked in. Status: ${status}`);
            }
        } else { // Check-out
            const logSnap = await getDoc(logRef);
            if (logSnap.exists() && !logSnap.data().checkOut) {
                await updateDoc(logRef, { checkOut: Timestamp.now(), status: 'Completed', checkOutLocation: locationData });
                toast.success(`${employee.fullName} checked out.`);
            } else { toast.error(`${employee.fullName} not checked in yet or already checked out.`); }
        }
        resetKiosk();
    };
    
    const isCheckedIn = recognizedEmployee ? logData.some(log => log.employeeId === recognizedEmployee.id && log.checkIn && !log.checkOut) : false;
    const isCheckedOut = recognizedEmployee ? logData.some(log => log.employeeId === recognizedEmployee.id && log.checkOut) : false;
    
    const summary = {
        totalEmployees: employees.length,
        present: logData.filter(entry => ['Present', 'Late', 'Completed'].includes(entry.status)).length,
        late: logData.filter(entry => entry.status === 'Late').length,
        absent: employees.length - logData.filter(entry => ['Present', 'Late', 'Completed', 'Holiday'].includes(entry.status)).length,
        holiday: logData.filter(entry => entry.status === 'Holiday').length,
        onLeave: 0,
    };

    return (
        <div className="container mx-auto">
            <div className="mb-8 flex justify-between items-center">
                <div><h1 className="admin-page-title">Live Attendance Kiosk</h1><p className="admin-page-sub">For Flare Magazine Team</p></div>
                <div className="text-right">
                    <p className="text-4xl font-bold text-red-600">{time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                    <p className="text-base text-gray-500">{time.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-md flex flex-col">
                    <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
                        <video ref={videoRef} onPlay={handleVideoPlay} autoPlay muted playsInline className="w-full h-full object-cover transform scale-x-[-1]"></video>
                        
                        {recognizedEmployee && (
                            <div className="absolute inset-0 p-6 bg-gradient-to-t from-black/80 to-transparent flex items-end justify-between">
                                <div className="flex items-center gap-4">
                                    <img src={recognizedEmployee.photoURL} className="w-24 h-24 rounded-full border-4 border-white shadow-lg" alt={recognizedEmployee.fullName} />
                                    <div>
                                        <p className="text-lg text-white">Welcome</p>
                                        <p className="text-3xl font-bold text-white">{recognizedEmployee.fullName}</p>
                                        <div className="mt-4 flex gap-4">
                                            <button onClick={() => handleAttendanceAction('check-in')} disabled={isCheckedIn || isCheckedOut} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-gray-400">Check-In</button>
                                            <button onClick={() => handleAttendanceAction('check-out')} disabled={!isCheckedIn || isCheckedOut} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-gray-400">Check-Out</button>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={resetKiosk} className="self-start p-2 bg-white/20 text-white rounded-full hover:bg-white/40">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="mt-4 bg-gray-100 px-6 py-3 rounded-lg flex items-center gap-4">
                        <div className={`w-3.5 h-3.5 rounded-full ${isCameraOn ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                        <p className="text-lg font-medium">{statusText}</p>
                    </div>
                </div>
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-xl shadow-md">
                        <h3 className="font-bold text-lg text-gray-700 mb-4">System Controls</h3>
                        <div className="space-y-4">
                            {!isCameraOn ? 
                                <button onClick={startWebcam} disabled={!faceApiModelsLoaded} className="w-full bg-red-600 text-white font-bold py-3 px-4 rounded-lg" style={{backgroundColor: '#c4151c'}}>
                                    {faceApiModelsLoaded ? 'Start Camera' : 'Loading Models...'}
                                </button> : 
                                <button onClick={stopWebcam} className="w-full bg-gray-700 text-white font-bold py-3 px-4 rounded-lg">Stop Camera</button>
                            }
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <h4 className="font-bold text-sm text-gray-700 mb-2">Current Location</h4>
                            <div className="flex items-start gap-3">
                                <span className="text-blue-500 mt-1">📍</span>
                                <div>
                                    {currentLocation.status === 'success' && currentLocation.data ? (<><p className="text-sm font-semibold text-gray-800">{currentLocation.data.name}</p><p className="text-xs text-gray-500">Lat: {currentLocation.data.latitude.toFixed(4)}, Lon: {currentLocation.data.longitude.toFixed(4)}</p></>) 
                                    : currentLocation.status === 'fetching' ? (<p className="text-xs text-gray-500 italic">Fetching location...</p>) 
                                    : (<p className="text-xs text-red-500">Location not available.</p>)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-md">
                        <h3 className="font-bold text-lg text-gray-700 mb-4">Today's Summary</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-green-50 p-4 rounded-lg text-center"><p className="text-sm font-semibold text-green-700">Present</p><p className="text-3xl font-extrabold">{summary.present}</p></div>
                            <div className="bg-red-50 p-4 rounded-lg text-center"><p className="text-sm font-semibold text-red-700">Absent</p><p className="text-3xl font-extrabold">{summary.absent}</p></div>
                            <div className="bg-yellow-50 p-4 rounded-lg text-center"><p className="text-sm font-semibold text-yellow-700">Late</p><p className="text-3xl font-extrabold">{summary.late}</p></div>
                            <div className="bg-blue-50 p-4 rounded-lg text-center"><p className="text-sm font-semibold text-blue-700">Holiday</p><p className="text-3xl font-extrabold">{summary.holiday}</p></div>
                        </div>
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <Link to="/attendance/report" className="w-full bg-gray-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-gray-700 flex justify-center items-center">View Monthly Report</Link>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-white rounded-xl shadow-md overflow-hidden mt-8">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">Attendance Log for Today</h2>
                    <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="bg-gray-50 border-gray-300 rounded-lg p-2 font-semibold"/>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                            <tr><th className="px-6 py-3 text-left">Employee</th><th className="px-6 py-3 text-left">Check-in</th><th className="px-6 py-3 text-left">Check-out</th><th className="px-6 py-3 text-center">Status</th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {logData.length > 0 ? logData.map(log => (
                                <tr key={log.id}>
                                    <td className="px-6 py-4 flex items-center gap-3"><img src={log.photoURL || 'https://placehold.co/40x40/E2E8F0/4A5568?text=P'} className="w-10 h-10 rounded-full object-cover" alt={log.fullName} onError={(e) => { e.target.src='https://placehold.co/40x40/E2E8F0/4A5568?text=P'; }}/><div><div className="font-semibold text-gray-800">{log.fullName}</div></div></td>
                                    <td className="px-6 py-4 text-gray-600">{log.checkIn ? log.checkIn.toDate().toLocaleTimeString() : '--:--'}</td>
                                    <td className="px-6 py-4 text-gray-600">{log.checkOut ? log.checkOut.toDate().toLocaleTimeString() : '--:--'}</td>
                                    <td className="px-6 py-4 text-center"><span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${log.status === 'Completed' ? 'bg-blue-100 text-blue-800' : log.status === 'Late' ? 'bg-yellow-100 text-yellow-800' : log.status === 'Holiday' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>{log.status}</span></td>
                                </tr>
                            )) : <tr><td colSpan="4" className="text-center p-8 text-gray-500">No attendance records for this date.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default LiveKioskSection;